/**
 * Design System Grid
 */

import { BreakPoint, MEDIA_QUERY, pxToRem } from '@bucketplace/design-system';
import {
  COLUMN_PRESET,
  GridItemProps,
  TOTAL_COLUMN,
} from '@bucketplace/design-system/bds';
import { css, SerializedStyles } from '@emotion/react';

export type Gap =
  | number
  | string
  | {
      mobile?: number | string;
      tablet?: number | string;
      laptop?: number | string;
    };

export interface GridGap {
  row: string;
  column: string;
}

export type ColumnPreset = 'full' | 'half' | 'quarter';

export function cssGridGap(gap: Gap): SerializedStyles {
  if (typeof gap === 'object') {
    const { mobile = 0, tablet = mobile, laptop = tablet } = gap;

    return css`
      ${mapToGrid(baseGap(mobile))};
      ${MEDIA_QUERY.min('tablet', mapToGrid(baseGap(tablet)))}
      ${MEDIA_QUERY.min('laptop', mapToGrid(baseGap(laptop)))}
    `;
  }
  return css`${mapToGrid(baseGap(gap))};`;
}

export function cssGridItemGap(gap: Gap): SerializedStyles {
  if (typeof gap === 'object') {
    const { mobile = 0, tablet = mobile, laptop = tablet } = gap;

    return css`
      ${mapToGridItem(baseGap(mobile))};
      ${MEDIA_QUERY.min('tablet', mapToGridItem(baseGap(tablet)))}
      ${MEDIA_QUERY.min('laptop', mapToGridItem(baseGap(laptop)))}
    `;
  }
  return css`${mapToGrid(baseGap(gap))};`;
}
export function cssColumn(column: GridItemProps['column']): SerializedStyles {
  const {
    mobile = 1,
    tablet = mobile,
    laptop = tablet,
  } = typeof column === 'object' ? column : COLUMN_PRESET[column];

  return css`
    ${mapToColumn('mobile', mobile)};
    ${mapToColumn('tablet', tablet)};
    ${mapToColumn('laptop', laptop)};
  `;
}

function baseGap(gap: number | string): GridGap {
  const gaps = `${gap}`.split(' ').map(Number);
  const vertical = gaps[0] as number;
  const horizontal = gaps[1];

  if (typeof horizontal === 'number') {
    return {
      row: pxToRem(vertical / 2),
      column: pxToRem(horizontal / 2),
    };
  }

  const halfGap = pxToRem(vertical / 2);
  return {
    row: halfGap,
    column: halfGap,
  };
}

function mapToGrid(gap: GridGap): SerializedStyles {
  return css`margin: -${gap.row} -${gap.column};`;
}

function mapToGridItem(gap: GridGap): SerializedStyles {
  return css`padding: ${gap.row} ${gap.column};`;
}

function mapToColumn(
  breakPoint: BreakPoint,
  column: number | ColumnPreset,
): SerializedStyles {
  const size =
    typeof column === 'number' ? column : COLUMN_PRESET[column][breakPoint];

  const width = (100 / TOTAL_COLUMN[breakPoint]) * size;

  return breakPoint === 'mobile'
    ? css`width: ${width}%;`
    : css`${MEDIA_QUERY.min(breakPoint, `width: ${width}%;`)}`;
}
